import { lazy, Suspense } from 'react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from '@/context/AuthProvider';
import { SubscriptionProvider } from '@/context/SubscriptionContext';
import { LanguageProvider } from '@/context/LanguageContext';
import { GlobalMusicProvider } from '@/context/GlobalMusicContext';
import { queryClient } from '@/lib/query-client';
import '@/index.css';
import * as labels from "@/paraglide/messages";
import { createClient } from '@supabase/supabase-js';
import { registerSW } from 'virtual:pwa-register';
import { getMemoryMonitor } from '@/utils/memoryMonitor';
import { toast } from 'sonner';

// Start memory monitoring in dev mode
if (import.meta.env.DEV) {
    getMemoryMonitor().startMonitoring(60000); // Check every minute
}

// ── PWA AUTO-UPDATE LOGIC ──
registerSW({
    onNeedRefresh() {
        console.warn('[SSC PWA] New content available.');
        toast('Update Available', {
            description: 'A new version of the platform is ready.',
            action: {
                label: 'Refresh Now',
                onClick: () => window.location.reload()
            },
            duration: 15000, // Stays for 15s to ensure they see it
        });
    },
    onOfflineReady() {
        console.log('[SSC PWA] Offline Ready');
    },
});

// --- SHORTLINK INTERCEPTOR ---
if (window.location.hostname.includes('sscurl.com')) {
    // Show a sleek dark screen while routing so the main app doesn't flash
    document.body.innerHTML = '<div style="display:flex;justify-content:center;align-items:center;height:100vh;background:#05070A;color:#1A75FF;font-family:sans-serif;font-weight:bold;letter-spacing:0.2em;text-transform:uppercase;">Routing...</div>';

    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    const username = pathSegments[0];
    const queryParams = window.location.search;

    if (username) {
        (async () => {
            try {
                // Init a fast client just for this lookup
                const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
                const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
                const supabase = createClient(supabaseUrl, supabaseKey);

                const { data } = await supabase.from('storefronts')
                    .select('custom_domain, username_path')
                    .eq('username_path', username.toLowerCase())
                    .single();

                let destination = 'https://shootshareconnect.com'; // Fallback

                if (data) {
                    if (data.custom_domain) {
                        // Sovereign Tier: Send them to BYOD domain
                        destination = `https://${data.custom_domain}`;
                    } else {
                        // Base/Creator Tier: Send to platform subpath
                        destination = `https://shootshareconnect.com/${data.username_path}`;
                    }
                }

                // Sling them to the destination (preserving things like ?p=listing_slug)
                window.location.href = `${destination}${queryParams}`;
            } catch (err) {
                console.error('[SSC Router] Error:', err);
                window.location.href = 'https://shootshareconnect.com';
            }
        })();
    } else {
        // Bare domain (sscurl.com) -> send to main site
        window.location.href = 'https://shootshareconnect.com';
    }
} else {
    // --- ONLY RENDER THE REACT APP IF IT'S NOT A SHORTLINK ---
    const App = lazy(() => import('./App'));
    const StorefrontHome = lazy(() => import('./features/storefront/StorefrontHome'));

    // Handle stale asset errors on new deployments
    const handleAssetMismatch = (msg: string) => {
        if (msg.includes('Failed to fetch dynamically imported module')) {
            // Only reload if we are actually connected to the internet. 
            // If offline, it's just a network drop, not a deployment mismatch.
            if (navigator.onLine) {
                console.warn('[SSC] Asset mismatch — reloading for new deployment in 1.5s...');
                setTimeout(() => window.location.reload(), 1500);
            } else {
                console.warn('[SSC] Network dropped. Waiting for reconnection...');
            }
        }
    };

    window.addEventListener('error', (e) => handleAssetMismatch(e.message || ''), true);
    window.addEventListener('unhandledrejection', (e) => handleAssetMismatch(e.reason?.message || ''));

    // ── BOOT SEQUENCE MONITOR ──
    (function monitorBoot() {
        const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        const reloadType = nav?.type || 'unknown';
        console.log(`[SSC Boot] Terminal Initialized. Mode: ${import.meta.env.MODE}. Type: ${reloadType}`);
        
        if (reloadType === 'reload') {
            console.warn('[SSC Boot] This was a full page reload.');
        }
    })();

    // ── Affiliate ?ref= capture ───────────────────────────────────────────────────
    (function captureReferralCode() {
        const ref = new URLSearchParams(window.location.search).get('ref');
        if (ref && ref.startsWith('SSC-')) {
            localStorage.setItem('ssc_ref_code', ref);
        }
    })();

    const GlobalLoader = () => (
        <div className="min-h-screen bg-brand-ink flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
                <div className="w-10 h-10 border-2 border-brand-blue border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground font-medium tracking-widest uppercase">{labels.loading()}</p>
            </div>
        </div>
    );

    // Routing logic: detect if this is a custom domain storefront
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;

    const isMarketplace =
        hostname === 'shootshareconnect.com' ||
        hostname === 'www.shootshareconnect.com' ||
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.endsWith('.vercel.app');

    // System paths always render main App even on custom domains
    const isSystemPath =
        pathname.startsWith('/login') ||
        pathname.startsWith('/pricing') ||
        pathname.startsWith('/auth') ||
        pathname.startsWith('/seller/') ||
        pathname.startsWith('/messages') ||
        pathname.startsWith('/profile') ||
        pathname.startsWith('/settings') ||
        pathname.startsWith('/orders') ||
        pathname.startsWith('/help') ||
        pathname.startsWith('/enterprise') ||
        pathname.startsWith('/church') ||
        pathname.startsWith('/invite/') ||
        pathname.startsWith('/claim/') ||   // Gift claim pages must render App, not StorefrontHome
        pathname.startsWith('/gift/') ||    // Alias route for gift claims (vault-generated links)
        pathname.startsWith('/vault/') ||   // Vault redirect post-claim
        pathname.startsWith('/purchases');  // Fallback for non-vaulted claims

    ReactDOM.createRoot(document.getElementById('root')!).render(
        <React.StrictMode>
            <HelmetProvider>
                <QueryClientProvider client={queryClient}>
                    <AuthProvider>
                        <SubscriptionProvider>
                            <LanguageProvider>
                                <GlobalMusicProvider>
                                    <BrowserRouter>
                                        <Suspense fallback={<GlobalLoader />}>
                                            {isMarketplace || isSystemPath ? (
                                                <App />
                                            ) : (
                                                <StorefrontHome />
                                            )}
                                        </Suspense>
                                    </BrowserRouter>
                                </GlobalMusicProvider>
                            </LanguageProvider>
                        </SubscriptionProvider>
                    </AuthProvider>
                </QueryClientProvider>
            </HelmetProvider>
        </React.StrictMode>,
    );
}
